// One list of job statuses for the server and the web app.
// The old mobile app is not updated here. It is due to be rebuilt.

const JOB_STATUSES = Object.freeze([
  { value: 'New', label: 'Unscheduled', color: 'gray', tailwind: 'bg-gray-100 text-gray-800', icon: '📋', sort: 1 },
  { value: 'Scheduled', label: 'Scheduled', color: 'blue', tailwind: 'bg-blue-100 text-blue-800', icon: '📅', sort: 2 },
  { value: 'Travelling', label: 'Travelling', color: 'amber', tailwind: 'bg-amber-100 text-amber-800', icon: '🚗', sort: 3 },
  { value: 'On site', label: 'On site', color: 'purple', tailwind: 'bg-purple-100 text-purple-800', icon: '🔧', sort: 4 },
  { value: 'On hold (waiting parts)', label: 'On hold', color: 'red', tailwind: 'bg-red-100 text-red-800', icon: '⏸️', sort: 5 },
  { value: 'Completed', label: 'Completed', color: 'green', tailwind: 'bg-green-100 text-green-800', icon: '✅', sort: 6 },
  { value: 'Invoiced', label: 'Invoiced', color: 'teal', tailwind: 'bg-teal-100 text-teal-800', icon: '💰', sort: 7 },
  { value: 'Cancelled', label: 'Cancelled', color: 'slate', tailwind: 'bg-gray-700 text-white', icon: '🚫', sort: 8 },
]);

const COLOUR_SWATCHES = Object.freeze({
  red: {
    badge: 'bg-red-100 text-red-800',
    block: 'bg-red-100 border-red-300 text-red-900 hover:bg-red-200',
    chip: 'bg-red-100 border-red-300 text-red-900',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-800',
    block: 'bg-orange-100 border-orange-300 text-orange-900 hover:bg-orange-200',
    chip: 'bg-orange-100 border-orange-300 text-orange-900',
  },
  yellow: {
    badge: 'bg-yellow-100 text-yellow-800',
    block: 'bg-yellow-100 border-yellow-300 text-yellow-900 hover:bg-yellow-200',
    chip: 'bg-yellow-100 border-yellow-300 text-yellow-900',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-800',
    block: 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200',
    chip: 'bg-amber-100 border-amber-300 text-amber-900',
  },
  green: {
    badge: 'bg-green-100 text-green-800',
    block: 'bg-green-100 border-green-300 text-green-900 hover:bg-green-200',
    chip: 'bg-green-100 border-green-300 text-green-900',
  },
  blue: {
    badge: 'bg-blue-100 text-blue-800',
    block: 'bg-blue-100 border-blue-300 text-blue-900 hover:bg-blue-200',
    chip: 'bg-blue-100 border-blue-300 text-blue-900',
  },
  purple: {
    badge: 'bg-purple-100 text-purple-800',
    block: 'bg-purple-100 border-purple-300 text-purple-900 hover:bg-purple-200',
    chip: 'bg-purple-100 border-purple-300 text-purple-900',
  },
  pink: {
    badge: 'bg-pink-100 text-pink-800',
    block: 'bg-pink-100 border-pink-300 text-pink-900 hover:bg-pink-200',
    chip: 'bg-pink-100 border-pink-300 text-pink-900',
  },
  indigo: {
    badge: 'bg-indigo-100 text-indigo-800',
    block: 'bg-indigo-100 border-indigo-300 text-indigo-900 hover:bg-indigo-200',
    chip: 'bg-indigo-100 border-indigo-300 text-indigo-900',
  },
  teal: {
    badge: 'bg-teal-100 text-teal-800',
    block: 'bg-teal-100 border-teal-300 text-teal-900 hover:bg-teal-200',
    chip: 'bg-teal-100 border-teal-300 text-teal-900',
  },
  gray: {
    badge: 'bg-gray-100 text-gray-800',
    block: 'bg-gray-100 border-gray-300 text-gray-900 hover:bg-gray-200',
    chip: 'bg-gray-100 border-gray-300 text-gray-900',
  },
  slate: {
    badge: 'bg-gray-700 text-white',
    block: 'bg-gray-700 border-gray-800 text-white hover:bg-gray-600',
    chip: 'bg-gray-700 border-gray-800 text-white',
  },
});

const COLOUR_NAMES = Object.freeze(Object.keys(COLOUR_SWATCHES));

const JOB_STATUS = Object.freeze({
  NEW: 'New',
  SCHEDULED: 'Scheduled',
  TRAVELLING: 'Travelling',
  ON_SITE: 'On site',
  ON_HOLD: 'On hold (waiting parts)',
  COMPLETED: 'Completed',
  INVOICED: 'Invoiced',
  CANCELLED: 'Cancelled',
});

const JOB_STATUS_VALUES = Object.freeze(JOB_STATUSES.map((status) => status.value));
const DEFAULT_JOB_STATUS = JOB_STATUS.NEW;
const CLOSED_JOB_STATUSES = Object.freeze([
  JOB_STATUS.COMPLETED,
  JOB_STATUS.INVOICED,
  JOB_STATUS.CANCELLED,
]);

const STATUS_ALIASES = Object.freeze({
  new: JOB_STATUS.NEW,
  pending: JOB_STATUS.NEW,
  unassigned: JOB_STATUS.NEW,
  open: JOB_STATUS.NEW,
  draft: JOB_STATUS.NEW,
  scheduled: JOB_STATUS.SCHEDULED,
  travelling: JOB_STATUS.TRAVELLING,
  traveling: JOB_STATUS.TRAVELLING,
  'en route': JOB_STATUS.TRAVELLING,
  'on route': JOB_STATUS.TRAVELLING,
  'on the way': JOB_STATUS.TRAVELLING,
  'on site': JOB_STATUS.ON_SITE,
  onsite: JOB_STATUS.ON_SITE,
  'in progress': JOB_STATUS.ON_SITE,
  inprogress: JOB_STATUS.ON_SITE,
  started: JOB_STATUS.ON_SITE,
  active: JOB_STATUS.ON_SITE,
  'on hold': JOB_STATUS.ON_HOLD,
  'on hold (waiting parts)': JOB_STATUS.ON_HOLD,
  'waiting parts': JOB_STATUS.ON_HOLD,
  'waiting for parts': JOB_STATUS.ON_HOLD,
  'awaiting parts': JOB_STATUS.ON_HOLD,
  completed: JOB_STATUS.COMPLETED,
  complete: JOB_STATUS.COMPLETED,
  done: JOB_STATUS.COMPLETED,
  invoiced: JOB_STATUS.INVOICED,
  cancelled: JOB_STATUS.CANCELLED,
  canceled: JOB_STATUS.CANCELLED,
});

function aliasKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function normalizeJobStatus(value) {
  if (value === undefined || value === null) return null;
  const key = aliasKey(value);
  if (!key) return null;
  return STATUS_ALIASES[key] || null;
}

function jobStatusForStorage(value) {
  if (value === undefined || value === null || String(value).trim() === '') return DEFAULT_JOB_STATUS;
  return normalizeJobStatus(value) || String(value).trim();
}

function statusRecord(status) {
  const canonical = normalizeJobStatus(status);
  return JOB_STATUSES.find((item) => item.value === canonical) || null;
}

function displayLabel(status) {
  const found = statusRecord(status);
  if (found) return found.label;
  if (status === undefined || status === null || String(status).trim() === '') {
    return JOB_STATUSES[0].label;
  }
  return String(status).trim();
}

function labelFor(status) {
  return displayLabel(status);
}

function statusIcon(status) {
  const found = statusRecord(status);
  return found ? found.icon : '📋';
}

function statusTailwind(status) {
  const found = statusRecord(status);
  return found ? found.tailwind : 'bg-gray-100 text-gray-700';
}

function statusColor(status) {
  const found = statusRecord(status);
  return found ? found.color : 'gray';
}

function colourLookup(rows) {
  const lookup = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || !row.status_name) return;
    lookup[row.status_name] = {
      color: row.timeline_color || row.display_color || null,
      icon: row.icon || null,
    };
  });
  return lookup;
}

function appearance(status, lookup) {
  const canonical = normalizeJobStatus(status);
  const company = lookup && (lookup[status] || (canonical && lookup[canonical])) || null;
  const named = company && company.color && COLOUR_SWATCHES[company.color]
    ? company.color
    : statusColor(status);
  const swatch = COLOUR_SWATCHES[named] || COLOUR_SWATCHES.gray;
  return {
    status: canonical || DEFAULT_JOB_STATUS,
    label: displayLabel(status),
    icon: (company && company.icon) || statusIcon(status),
    color: named,
    badgeClass: swatch.badge,
    blockClass: swatch.block,
  };
}

function matchesStatusFilter(status, filter) {
  if (!filter || filter === 'all') return true;
  const canonical = normalizeJobStatus(status);
  return canonical === filter || status === filter;
}

function isCompleted(status) {
  return normalizeJobStatus(status) === JOB_STATUS.COMPLETED;
}

function isClosed(status) {
  const canonical = normalizeJobStatus(status);
  return CLOSED_JOB_STATUSES.includes(canonical);
}

function statusMigrationPlan(rows) {
  const changes = [];
  const unmapped = [];
  for (const row of rows || []) {
    const from = row.job_status;
    if (from === undefined || from === null || String(from).trim() === '') continue;
    const count = Number(row.count ?? row.n ?? 1);
    const to = normalizeJobStatus(from);
    if (!to) unmapped.push({ from, count });
    else if (to !== from) changes.push({ from, to, count });
  }
  return { changes, unmapped };
}

module.exports = {
  CLOSED_JOB_STATUSES,
  COLOUR_NAMES,
  COLOUR_SWATCHES,
  DEFAULT_JOB_STATUS,
  JOB_STATUS,
  JOB_STATUS_VALUES,
  JOB_STATUSES,
  STATUS_ALIASES,
  appearance,
  colourLookup,
  displayLabel,
  isClosed,
  isCompleted,
  jobStatusForStorage,
  labelFor,
  matchesStatusFilter,
  normalizeJobStatus,
  statusColor,
  statusIcon,
  statusMigrationPlan,
  statusTailwind,
};
